import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Star,
  Phone,
  ShieldCheck,
  Zap,
  Users,
  Award,
  PoundSterling,
  MapPin,
  Wrench,
  FileText,
  Camera,
  Scale,
  ClipboardCheck,
  Hammer,
  FileCheck,
  Flame,
  BadgeCheck,
  Key,
  LogOut,
  Search,
  Gavel,
  Briefcase,
  Shield,
  TrendingUp,
  Lock,
  RefreshCcw,
  Eye,
  Download,
  Loader2,
  X as XIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { ServiceEnquiryDialog } from "../components/services/ServiceEnquiryDialog";
import { PackagesSEO } from "../components/SEO";
import { toast } from "sonner";

const PACKAGES = [
  {
    id: "bronze",
    name: "Bronze",
    subtitle: "Tenant Find Only",
    accent: "from-amber-700 to-amber-900",
    badge: null,
    highlight: false,
    premium: false,
    price: "One Month's Rent",
    priceSuffix: "+ VAT",
    priceMinFee: "Minimum £695 + VAT",
    description:
      "Ideal for landlords who want to self-manage while we source, reference and secure high-quality tenants.",
    features: [
      "Professional property marketing",
      "Professional photography",
      "Online advertising on major portals",
      "Accompanied viewings",
      "Tenant referencing",
      "Right to Rent checks",
      "Tenancy agreement preparation",
      "Deposit registration",
      "Move-in coordination",
      "Utility notification service",
      "Initial compliance review",
    ],
    notIncluded: [
      "Ongoing rent collection",
      "Arrears management",
      "Routine property management",
      "Maintenance management",
      "Ongoing tenant management",
    ],
    optionalExtras: [
      "Inventory preparation",
      "Check-in service",
      "EPC arrangement",
      "Gas Safety Certificate arrangement",
      "EICR arrangement",
    ],
    ctaLabel: "Find Me a Tenant",
    ctaPath: "/contact?package=bronze",
  },
  {
    id: "silver",
    name: "Silver",
    subtitle: "Rent Collection",
    accent: "from-slate-400 to-slate-600",
    badge: null,
    highlight: false,
    premium: false,
    price: "7% + VAT",
    priceSuffix: "per month",
    description:
      "Perfect for landlords who want financial management support while retaining control of maintenance and day-to-day property management.",
    features: [
      "Monthly rent collection",
      "Monthly landlord statements",
      "Payment monitoring",
      "Arrears monitoring and chasing",
      "Tenant communication relating to rent",
      "Rent review advice",
      "Statutory rent increase administration",
      "Landlord reporting",
      "Accounts support",
    ],
    notIncluded: [
      "Maintenance coordination",
      "Contractor management",
      "Property inspections",
      "Compliance monitoring",
    ],
    includedValue:
      "Rent increases are handled subject to the applicable statutory process and legal requirements.",
    ctaLabel: "Start Rent Collection",
    ctaPath: "/contact?package=silver",
  },
  {
    id: "gold",
    name: "Gold",
    subtitle: "Fully Managed",
    accent: "from-gold to-amber-500",
    badge: "Most Popular",
    highlight: true,
    premium: true,
    price: "10% + VAT",
    priceSuffix: "per month",
    onboardingFee: {
      label: "Initial Management Onboarding",
      price: "£295 + VAT (one-off)",
      description:
        "One-off fee for onboarding a property onto our fully managed service, reviewing existing tenancy and property documentation, setting up landlord and tenant records, establishing compliance records and completing the management handover.",
    },
    description:
      "Complete hands-off property management designed for landlords who want professional management with minimal involvement.",
    features: [
      "Everything in Silver",
      "Full tenant management",
      "Maintenance management",
      "Routine contractor coordination",
      "Routine property inspections",
      "Compliance monitoring",
      "Deposit administration",
      "Ongoing tenancy administration",
      "Checkout coordination",
      "Rent assessment and market advice",
      "Tenant issue resolution",
      "Contractor invoice coordination",
      "Compliance reminders",
      "Landlord support",
      "Utility and council correspondence where appropriate",
    ],
    includedValue: "Landlords deal with Keystone Properties, not the tenant.",
    ctaLabel: "Get Full Management",
    ctaPath: "/contact?package=gold",
  },
  {
    id: "platinum",
    name: "Platinum",
    subtitle: "Investor & HMO Management",
    accent: "from-slate-800 to-slate-950",
    badge: "Premium",
    highlight: false,
    premium: false,
    price: "15% + VAT",
    priceSuffix: "per month",
    priceMinFee: "Minimum £150 PCM",
    onboardingFee: {
      label: "Initial Investor & HMO Onboarding",
      price: "From £395 + VAT (one-off)",
      description:
        "One-off onboarding and setup service for investor and HMO properties, including management handover, compliance review, property setup and portfolio documentation.",
    },
    description:
      "Premium management for investors, HMOs, complex properties and portfolio landlords.",
    features: [
      "Everything in Gold",
      "HMO management",
      "HMO compliance coordination",
      "Fire safety coordination",
      "HMO inspections",
      "Licensing support",
      "Communal area management",
      "Cleaner coordination",
      "Refurbishment / project oversight",
      "Portfolio reporting",
      "Investment / property performance advice",
      "Strategic landlord support",
    ],
    includedValue:
      "Designed for landlords with multiple properties who require a proactive management partner rather than a traditional letting agent.",
    ctaLabel: "Speak To An Investor Specialist",
    ctaPath: "/contact?package=platinum",
  },
];

// Category order also drives display order in the grouped view.
const SERVICE_CATEGORIES = [
  "Compliance & Property Services",
  "Tenancy Administration",
  "Onboarding & Property Transfer",
  "Attendance & Visits",
  "HMO & Licensing",
  "Refurbishment & Insurance",
  "Possession & Legal Coordination",
  "Reporting & Investment",
];

const ADDITIONAL_SERVICES = [
  // --- Compliance & Property Services ---
  {
    id: "epc-arrangement",
    icon: FileCheck,
    title: "EPC Arrangement",
    description:
      "We coordinate Energy Performance Certificates through qualified local assessors.",
    price: "From £95",
    category: "Compliance & Property Services",
  },
  {
    id: "gas-safety-certificate-arrangement",
    icon: Flame,
    title: "Gas Safety Certificate",
    description:
      "Annual gas safety inspections arranged through qualified Gas Safe engineers.",
    price: "From £85",
    category: "Compliance & Property Services",
  },
  {
    id: "eicr-arrangement",
    icon: Zap,
    title: "EICR Arrangement",
    description:
      "Electrical Installation Condition Reports arranged through certified contractors.",
    price: "From £180",
    category: "Compliance & Property Services",
  },
  {
    id: "inventory-preparation",
    icon: ClipboardCheck,
    title: "Inventory Preparation",
    description:
      "Comprehensive inventory reports designed to protect landlords and support deposit claims.",
    price: "From £95",
    category: "Compliance & Property Services",
  },
  {
    id: "check-in-service",
    icon: Key,
    title: "Check-In Service",
    description:
      "Professional tenant check-in appointments including documentation, meter readings and key handover.",
    price: "From £75",
    category: "Compliance & Property Services",
  },
  {
    id: "check-out-service",
    icon: LogOut,
    title: "Check-Out Service",
    description:
      "End-of-tenancy inspections and reporting to support deposit negotiations and property handover.",
    price: "From £95",
    category: "Compliance & Property Services",
  },
  {
    id: "property-inspection-service",
    icon: Search,
    title: "Property Inspection",
    description:
      "Routine property inspections with written reports and photographs.",
    price: "From £60",
    category: "Compliance & Property Services",
  },
  {
    id: "professional-photography",
    icon: Camera,
    title: "Professional Photography",
    description:
      "High-quality property photography to maximise enquiries and reduce void periods.",
    price: "From £75",
    category: "Compliance & Property Services",
  },
  {
    id: "key-holding-service",
    icon: Lock,
    title: "Key Holding",
    description:
      "Secure key holding for landlords requiring emergency access arrangements.",
    price: "From £10 PCM",
    category: "Compliance & Property Services",
  },

  // --- Tenancy Administration ---
  {
    id: "tenancy-variation-and-administration",
    icon: RefreshCcw,
    title: "Tenancy Variation & Administration",
    description:
      "Preparation and administration of permitted changes to tenancy terms, including tenancy variations and associated documentation.",
    price: "From £95 + VAT",
    category: "Tenancy Administration",
  },
  {
    id: "bespoke-landlord-reports",
    icon: FileText,
    title: "Bespoke Landlord Reports",
    description:
      "Historical rent statements, annual income reports, portfolio, accountant/lender or property performance reports. Standard monthly landlord statements remain included in Silver and Gold.",
    price: "From £50 + VAT",
    category: "Tenancy Administration",
  },

  // --- Onboarding & Property Transfer ---
  {
    id: "management-transfer-existing-tenancy-takeover",
    icon: RefreshCcw,
    title: "Management Transfer / Tenancy Takeover",
    description:
      "For properties transferred to Keystone Properties from another agent or landlord — management handover, document review, tenancy setup, compliance review and establishment of records.",
    price: "From £295 + VAT",
    category: "Onboarding & Property Transfer",
  },
  {
    id: "portfolio-onboarding",
    icon: Users,
    title: "Portfolio Onboarding",
    description:
      "Structured onboarding for landlords transferring multiple properties — 1 property £295 + VAT · 2–4 properties £250 + VAT each · 5+ properties £195 + VAT each. Bespoke pricing available for larger portfolios.",
    price: "From £195 + VAT / property",
    category: "Onboarding & Property Transfer",
  },

  // --- Attendance & Visits ---
  {
    id: "additional-property-attendance",
    icon: MapPin,
    title: "Additional Property Attendance",
    description:
      "Additional physical attendance at a property outside routine management — landlord-requested visits, contractor access, complaint investigations, insurance inspections or other exceptional attendance.",
    price: "From £50 + VAT",
    category: "Attendance & Visits",
  },
  {
    id: "landlord-requested-property-visit",
    icon: Eye,
    title: "Landlord-Requested Property Visit",
    description:
      "Additional property visit requested by the landlord, including basic photographic reporting where appropriate. Separate from routine inspections included in your package.",
    price: "From £60 + VAT",
    category: "Attendance & Visits",
  },

  // --- HMO & Licensing ---
  {
    id: "hmo-licence-application-assistance",
    icon: BadgeCheck,
    title: "HMO Licence Application & Compliance Support",
    description:
      "Support throughout the HMO licensing process including documentation, compliance guidance and council liaison.",
    price: "From £495",
    category: "HMO & Licensing",
  },
  {
    id: "hmo-licence-renewal",
    icon: RefreshCcw,
    title: "HMO Licence Renewal",
    description:
      "Preparation and submission of HMO licence renewal applications and supporting documentation.",
    price: "From £295",
    category: "HMO & Licensing",
  },
  {
    id: "hmo-licence-variation",
    icon: FileCheck,
    title: "HMO Licence Variation",
    description:
      "Amendments and variations to an existing HMO licence including documentation and council correspondence.",
    price: "From £195",
    category: "HMO & Licensing",
  },
  {
    id: "selective-additional-licensing-application",
    icon: BadgeCheck,
    title: "Selective / Additional Licensing Application",
    description:
      "Preparation and submission of selective or additional licensing applications in areas with council schemes.",
    price: "From £295",
    category: "HMO & Licensing",
  },
  {
    id: "council-inspection-preparation",
    icon: ClipboardCheck,
    title: "Council Inspection Preparation",
    description:
      "Preparation, documentation review and attendance coordination ahead of council or licensing authority inspections.",
    price: "From £150",
    category: "HMO & Licensing",
  },
  {
    id: "licensing-compliance-support",
    icon: ShieldCheck,
    title: "Licensing Compliance Support",
    description:
      "Ongoing licensing compliance tracking, reminders and documentation support for licensed properties.",
    price: "From £199/year",
    category: "HMO & Licensing",
  },

  // --- Refurbishment & Insurance ---
  {
    id: "refurbishment-project-management",
    icon: Hammer,
    title: "Refurbishment & Project Management",
    description:
      "End-to-end coordination of refurbishment and improvement projects, including contractor coordination, progress monitoring, landlord reporting and project administration. Routine maintenance coordination remains included in Gold.",
    price: "From 10% of project value",
    category: "Refurbishment & Insurance",
  },
  {
    id: "insurance-claim-management",
    icon: Shield,
    title: "Insurance Claim Management",
    description:
      "Management and coordination of landlord insurance claims — communication with insurers, loss adjusters, contractors and tenants, arranging access, gathering documentation and providing landlord updates.",
    price: "From £195 + VAT",
    category: "Refurbishment & Insurance",
  },

  // --- Possession & Legal Coordination ---
  {
    id: "deposit-dispute-management",
    icon: Scale,
    title: "Deposit Dispute Management",
    description:
      "Preparation and submission of evidence, communication with the relevant deposit scheme and administration of formal deposit disputes.",
    price: "From £95 + VAT",
    category: "Possession & Legal Coordination",
  },
  {
    id: "section-8-and-possession-notice-service",
    icon: Gavel,
    title: "Section 8 & Possession Support",
    description:
      "Preparation and coordination of possession notices and supporting documentation, subject to the applicable statutory grounds and procedures. Where specialist legal advice is required, Keystone can coordinate with appropriately qualified legal professionals.",
    price: "From £195 + VAT",
    category: "Possession & Legal Coordination",
  },
  {
    id: "possession-and-legal-case-coordination",
    icon: Briefcase,
    title: "Possession & Legal Case Coordination",
    description:
      "Administrative and case-management support during possession proceedings, including coordination with landlords, solicitors, courts and relevant third parties. Not regulated legal advice.",
    price: "From £95/hour + VAT",
    category: "Possession & Legal Coordination",
  },
  {
    id: "possession-case-coordination",
    icon: FileText,
    title: "Possession Case Coordination",
    description:
      "Fixed-scope case coordination for straightforward possession matters. Administrative service only — not legal advice.",
    price: "From £295 + VAT",
    category: "Possession & Legal Coordination",
  },
  {
    id: "court-hearing-attendance-coordination",
    icon: Gavel,
    title: "Court / Hearing Attendance Coordination",
    description:
      "Administrative attendance and coordination at hearings on the landlord's behalf. Coordination service — not legal representation.",
    price: "From £95/hour + VAT",
    category: "Possession & Legal Coordination",
  },
  {
    id: "bailiff-warrant-coordination",
    icon: ShieldCheck,
    title: "Bailiff / Warrant Coordination",
    description:
      "Administrative coordination of warrant applications and bailiff attendance for enforcement matters.",
    price: "From £150 + VAT",
    category: "Possession & Legal Coordination",
  },

  // --- Reporting & Investment ---
  {
    id: "annual-compliance-package",
    icon: ShieldCheck,
    title: "Annual Compliance Management",
    description:
      "Centralised monitoring, reminders and coordination of key property compliance requirements including EPC, gas safety, electrical safety and applicable licensing requirements. Certificates and statutory fees are separate.",
    price: "From £199/year",
    category: "Reporting & Investment",
  },
  {
    id: "rent-and-legal-protection",
    icon: Shield,
    title: "Rent & Legal Protection",
    description:
      "Support in arranging suitable rent guarantee and legal expenses products where available.",
    price: "Available as an optional service",
    category: "Reporting & Investment",
  },
  {
    id: "property-sourcing-for-investors",
    icon: TrendingUp,
    title: "Property Sourcing for Investors",
    description:
      "Separate investment property sourcing service — includes only the services specified within the agreed sourcing package.",
    price: "From £2,500",
    category: "Reporting & Investment",
  },
];

const TRUST_POINTS = [
  { icon: Zap, title: "Fast Communication", description: "Same-day responses across email, phone and WhatsApp." },
  { icon: Users, title: "Professional Tenant Sourcing", description: "Thorough referencing, Right-to-Rent and credit checks on every let." },
  { icon: PoundSterling, title: "Investor-Focused Service", description: "Yields, cash-flow reporting and portfolio insights tailored to investors." },
  { icon: ShieldCheck, title: "Compliance Expertise", description: "Gas Safety, EICR, EPC, HMO licensing and deposit protection — all handled." },
  { icon: MapPin, title: "Experienced Local Team", description: "West Yorkshire specialists who know every postcode we manage." },
  { icon: FileText, title: "Transparent Pricing", description: "Clear monthly statements with no hidden fees or surprise charges." },
  { icon: Wrench, title: "Trusted Contractor Network", description: "Vetted electricians, plumbers and trades on call 24/7." },
];

const STATS = [
  { value: "100+", label: "Managed Properties" },
  { value: "24/7", label: "Emergency Support" },
  { value: "98%", label: "Rent Collection Rate" },
];

const FAQS = [
  {
    q: "Which package is right for me?",
    a: "It depends on how hands-on you want to be. Bronze is best if you'll self-manage day-to-day; Silver handles rent and finances; Gold gives you full property management with a single point of contact; Platinum is designed for investors, HMOs and portfolio landlords.",
  },
  {
    q: "How does your pricing work — are there hidden fees?",
    a: "We believe in transparency rather than pretending every service is included. Management fees are clearly set: Bronze one month's rent (min £695 + VAT), Silver 7% + VAT, Gold 10% + VAT, Platinum 15% + VAT (min £150 PCM). Gold includes a one-off £295 + VAT initial onboarding fee; Platinum onboarding is from £395 + VAT. Specialist, exceptional or project-based services (major refurbishments, possession proceedings, licensing applications, etc.) are priced separately and will be clearly explained — and, wherever reasonably possible, agreed with you before work is undertaken.",
  },
  {
    q: "How do rent increases work under the new rental framework?",
    a: "Under the current legal framework in England we handle rent reviews subject to the applicable statutory process. Silver and Gold include statutory rent increase administration — we don't operate on the basis of routine annual AST renewals.",
  },
  {
    q: "What happens if I need help with a possession matter?",
    a: "We offer administrative and case-management coordination for possession matters — preparing Section 8 documentation, coordinating with your solicitors and courts, and arranging bailiff/warrant actions where needed. Keystone provides coordination and support, not regulated legal advice; where specialist legal advice is required, we coordinate with appropriately qualified legal professionals.",
  },
  {
    q: "What is the onboarding fee for?",
    a: "It's a one-off charge for setting a property up on our fully managed service — reviewing existing documentation, establishing landlord/tenant records, creating compliance records and completing the management handover. It particularly applies where we're taking over from another agent or a self-managing landlord.",
  },
  {
    q: "Do you manage HMOs?",
    a: "Yes — our Platinum package is built specifically for HMOs and investor portfolios. We handle HMO management, licensing support, fire safety coordination, communal area management, cleaner coordination and portfolio-level reporting.",
  },
  {
    q: "Can I switch packages later?",
    a: "Yes. Landlords often start with Silver and upgrade to Gold as their portfolio grows. Contact us any time to discuss.",
  },
];

const PackagesPage = () => {
  const [enquiryService, setEnquiryService] = useState(null);
  const [serviceQuery, setServiceQuery] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const location = useLocation();

  const handleDownloadPricingPdf = async () => {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      // Lazy-load so jsPDF (~150KB) stays out of the initial bundle.
      const { generatePricingPdf } = await import("../lib/generatePricingPdf");
      await generatePricingPdf({
        packages: PACKAGES,
        additionalServices: ADDITIONAL_SERVICES,
        serviceCategories: SERVICE_CATEGORIES,
      });
      toast.success("Your 2026 pricing sheet is downloading…");
    } catch (err) {
      toast.error("Sorry, we couldn't generate the PDF. Please try again.");
      // eslint-disable-next-line no-console
      console.error("Pricing PDF error:", err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Hash-aware scroll: lets nav links like /services/packages#additional-services
  // jump straight to the right section after SPA navigation. react-router-dom's
  // <Link> doesn't auto-scroll to hash on its own.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // requestAnimationFrame lets the section finish mounting before we measure it.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash, location.key]);

  const filteredServices = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();
    if (!q) return ADDITIONAL_SERVICES;
    return ADDITIONAL_SERVICES.filter((svc) => {
      return (
        svc.title.toLowerCase().includes(q) ||
        svc.description.toLowerCase().includes(q)
      );
    });
  }, [serviceQuery]);

  return (
    <div data-testid="packages-page">
      <PackagesSEO />
      {/* Hero */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              Property Management
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              Property Management Packages
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Flexible management solutions tailored for landlords, investors and portfolio owners.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-gold hover:bg-gold-light text-navy font-semibold"
                data-testid="packages-hero-cta-consultation"
              >
                <Link to="/contact">
                  Book a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                data-testid="packages-hero-cta-compare"
              >
                <a href="#packages">Compare Packages</a>
              </Button>
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={handleDownloadPricingPdf}
                disabled={generatingPdf}
                className="border-white/40 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                data-testid="packages-hero-cta-pdf"
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 w-5 h-5" />
                    Download 2026 Pricing (PDF)
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="py-20 md:py-28 bg-white scroll-mt-28 lg:scroll-mt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Choose the package that fits your portfolio
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Professional property management with transparent, competitive pricing — from tenant-find only to full HMO portfolio management.
            </p>
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch"
            data-testid="packages-grid"
          >
            {PACKAGES.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>

          {/* Transparent Pricing message */}
          <div
            className="max-w-4xl mx-auto mt-14 md:mt-16 rounded-xl border border-slate-200 bg-off-white p-6 md:p-8"
            data-testid="transparent-pricing-note"
          >
            <div className="flex items-center gap-2 justify-center mb-3">
              <ShieldCheck className="w-5 h-5 text-gold" />
              <div className="text-xs font-semibold uppercase tracking-wider text-gold">
                Transparent Pricing · No Nasty Surprises
              </div>
            </div>
            <p className="text-slate-700 text-center leading-relaxed text-sm md:text-base">
              We believe landlords should know exactly what they are paying for. Our management packages clearly set out what is included, while specialist, exceptional and project-based services are priced separately where applicable. Any additional charge will be clearly explained and, wherever reasonably possible, agreed with you before work is undertaken.
            </p>
            <div className="mt-5 flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadPricingPdf}
                disabled={generatingPdf}
                className="border-navy/20 text-navy hover:bg-navy hover:text-white"
                data-testid="packages-pricing-note-pdf-cta"
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing your PDF…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download the full 2026 pricing sheet
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Fair Use / What "Fully Managed" Includes */}
      <section
        id="fully-managed-scope"
        className="py-20 md:py-24 bg-navy text-white scroll-mt-28 lg:scroll-mt-36"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-3 block">
              Fair Use — Scope of Gold
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              What does &ldquo;Fully Managed&rdquo; include?
            </h2>
            <p className="text-white/80 max-w-3xl mx-auto leading-relaxed">
              Gold provides comprehensive day-to-day property management, giving landlords a single point of contact for their property and tenants. Our management fee covers routine property management and administration. Certain specialist, exceptional or project-based services fall outside routine management and are charged separately where applicable — any additional charge will be clearly explained and agreed before work is undertaken wherever reasonably possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="fully-managed-scope-grid">
            {/* Included */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-6 md:p-7 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-gold" />
                <h3 className="font-heading text-lg font-semibold text-gold">Included within Gold</h3>
              </div>
              <ul className="space-y-2.5">
                {[
                  "Normal tenant communication",
                  "Routine maintenance coordination",
                  "Routine contractor coordination",
                  "Routine inspections",
                  "Rent collection",
                  "Arrears chasing",
                  "Compliance monitoring",
                  "Deposit administration",
                  "Standard landlord reporting",
                  "Standard tenancy administration",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-white/90">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* May incur additional charges */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-6 md:p-7 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-4">
                <PoundSterling className="w-5 h-5 text-white/80" />
                <h3 className="font-heading text-lg font-semibold text-white">
                  May incur additional charges
                </h3>
              </div>
              <ul className="space-y-2.5">
                {[
                  "Major refurbishment projects",
                  "Insurance claim management",
                  "HMO licensing applications",
                  "Selective / additional licensing applications",
                  "Council enforcement matters",
                  "Possession / eviction proceedings",
                  "Court-related work",
                  "Extensive damage investigations",
                  "Additional landlord-requested property visits",
                  "Specialist reports",
                  "Major project management",
                  "Exceptional regulatory / compliance work",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-white/80">
                    <span className="text-white/50 mt-0.5 flex-shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-center text-xs text-white/60 mt-8 max-w-3xl mx-auto italic leading-relaxed">
            We&apos;re not charging additional fees for normal day-to-day management — only for specialist, exceptional or project-based work outside the scope of routine management.
          </p>
        </div>
      </section>

      {/* Additional Services */}
      <section
        id="additional-services"
        className="py-20 md:py-28 bg-off-white scroll-mt-28 lg:scroll-mt-36"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              Additional Landlord Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Add the services you need, when you need them
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees — every service quoted up front before any work is carried out.
            </p>
          </div>

          {/* Search bar */}
          <div className="max-w-xl mx-auto mb-10 md:mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={serviceQuery}
                onChange={(e) => setServiceQuery(e.target.value)}
                placeholder="Search services… (e.g. EPC, HMO, photography)"
                className="pl-11 pr-11 h-12 bg-white border-slate-200 focus-visible:ring-gold/30 focus-visible:border-gold/60"
                data-testid="services-search-input"
                aria-label="Search additional landlord services"
              />
              {serviceQuery && (
                <button
                  type="button"
                  onClick={() => setServiceQuery("")}
                  aria-label="Clear search"
                  data-testid="services-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-navy transition-colors"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            {serviceQuery && (
              <p className="text-xs text-slate-500 text-center mt-3" data-testid="services-search-summary">
                Showing <span className="font-medium text-navy">{filteredServices.length}</span>
                {" "}of <span className="font-medium text-navy">{ADDITIONAL_SERVICES.length}</span> services
              </p>
            )}
          </div>

          {filteredServices.length > 0 ? (
            serviceQuery ? (
              // Flat filtered grid when searching
              <div
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
                data-testid="additional-services-grid"
              >
                {filteredServices.map((svc) => (
                  <ServiceCard
                    key={svc.id}
                    svc={svc}
                    onEnquire={() => setEnquiryService(svc)}
                  />
                ))}
              </div>
            ) : (
              // Grouped by category when no search
              <div className="space-y-14" data-testid="additional-services-grid">
                {SERVICE_CATEGORIES.map((cat) => {
                  const items = filteredServices.filter((s) => s.category === cat);
                  if (items.length === 0) return null;
                  return (
                    <div key={cat} data-testid={`service-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <div className="flex items-center gap-3 mb-6">
                        <div className="h-px flex-1 bg-slate-200" />
                        <h3 className="font-heading text-xl md:text-2xl font-semibold text-navy text-center whitespace-nowrap">
                          {cat}
                        </h3>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((svc) => (
                          <ServiceCard
                            key={svc.id}
                            svc={svc}
                            onEnquire={() => setEnquiryService(svc)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div
              className="text-center py-12 border border-dashed border-slate-200 rounded-lg bg-white max-w-xl mx-auto"
              data-testid="services-no-results"
            >
              <Search className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-1">
                No services match &ldquo;<span className="font-medium text-navy">{serviceQuery}</span>&rdquo;
              </p>
              <p className="text-sm text-slate-500 mb-4">Try a different keyword, or clear the search.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setServiceQuery("")}
                data-testid="services-no-results-clear"
              >
                Show all services
              </Button>
            </div>
          )}

          <p
            className="text-center text-xs text-slate-500 max-w-3xl mx-auto mt-10 italic leading-relaxed space-y-1"
            data-testid="services-price-disclaimer"
          >
            All prices shown are guide prices and may vary depending on property size, location, complexity and individual requirements. Third-party contractor costs, certificates, inspections and statutory fees (including council, licensing authority and other statutory fees) are separate and payable by the landlord where applicable.
          </p>
        </div>
      </section>

      {/* Compare Packages Table (mobile-friendly key differences) */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-3">
              Compare packages at a glance
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Key differences between Bronze, Silver, Gold and Platinum.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="packages-comparison-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-off-white border-b border-slate-200">
                  <th className="text-left font-heading font-semibold text-navy px-5 py-4">Feature</th>
                  <th className="text-center font-heading font-semibold text-navy px-4 py-4">Bronze</th>
                  <th className="text-center font-heading font-semibold text-navy px-4 py-4">Silver</th>
                  <th className="text-center font-heading font-semibold text-navy px-4 py-4 bg-gold/10 relative">
                    Gold
                    <div className="text-[10px] font-medium text-gold uppercase tracking-wider mt-0.5">
                      Most Popular
                    </div>
                  </th>
                  <th className="text-center font-heading font-semibold text-navy px-4 py-4">Platinum</th>
                </tr>
              </thead>
              <tbody className="[&_tr:not(:last-child)]:border-b [&_tr]:border-slate-100">
                {[
                  ["Tenant find & referencing", true, true, true, true],
                  ["Rent collection", false, true, true, true],
                  ["Rent Review & Statutory Rent Increase Administration", false, true, true, true],
                  ["Maintenance management", false, false, true, true],
                  ["Routine property inspections", false, false, true, true],
                  ["Compliance monitoring", false, false, true, true],
                  ["Ongoing Tenancy Administration", false, false, true, true],
                  ["Deposit administration", false, false, true, true],
                  ["Checkout coordination", false, false, true, true],
                  ["HMO management & compliance", false, false, false, true],
                  ["Fire safety coordination", false, false, false, true],
                  ["Refurbishment / project oversight", false, false, false, true],
                  ["Portfolio reporting", false, false, false, true],
                ].map(([label, b, s, g, p]) => (
                  <tr key={label}>
                    <td className="px-5 py-3 text-slate-700">{label}</td>
                    <td className="text-center px-4 py-3">
                      {b ? <Check className="w-4 h-4 text-navy inline" /> : <XIcon className="w-4 h-4 text-slate-300 inline" />}
                    </td>
                    <td className="text-center px-4 py-3">
                      {s ? <Check className="w-4 h-4 text-navy inline" /> : <XIcon className="w-4 h-4 text-slate-300 inline" />}
                    </td>
                    <td className="text-center px-4 py-3 bg-gold/5">
                      {g ? <Check className="w-4 h-4 text-gold inline" /> : <XIcon className="w-4 h-4 text-slate-300 inline" />}
                    </td>
                    <td className="text-center px-4 py-3">
                      {p ? <Check className="w-4 h-4 text-navy inline" /> : <XIcon className="w-4 h-4 text-slate-300 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            <div className="lg:col-span-1">
              <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
                Why Keystone
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-6">
                The local team landlords trust
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                We combine investor-grade reporting with on-the-ground West Yorkshire expertise — the same service whether you own one property or a portfolio of fifty.
              </p>
              <div className="grid grid-cols-3 gap-3 sm:gap-4" data-testid="packages-stats">
                {STATS.map((s) => (
                  <div key={s.label} className="text-center sm:text-left">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-navy">{s.value}</div>
                    <div className="text-xs sm:text-sm text-slate-500 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5" data-testid="trust-points-grid">
              {TRUST_POINTS.map(({ icon: Icon, title, description }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-11 h-11 flex-shrink-0 rounded-lg bg-navy/5 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-navy mb-1">{title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Everything you need to know before choosing a package.
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="packages-faq">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="border border-slate-200 rounded-lg bg-white px-5 sm:px-6"
              >
                <AccordionTrigger className="text-left font-heading text-base sm:text-lg font-semibold text-navy hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
            Ready to Simplify Your Property Management?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Speak to Keystone Properties today for a tailored management solution.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy font-semibold"
              data-testid="packages-final-cta-book"
            >
              <Link to="/contact">
                Book Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              data-testid="packages-final-cta-contact"
            >
              <Link to="/contact">
                <Phone className="mr-2 w-5 h-5" />
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Service enquiry modal — opens when an Additional Services card is clicked */}
      <ServiceEnquiryDialog
        open={!!enquiryService}
        onOpenChange={(open) => !open && setEnquiryService(null)}
        service={enquiryService}
      />
    </div>
  );
};

// ---------- Package Card ----------

const ServiceCard = ({ svc, onEnquire }) => {
  const Icon = svc.icon;
  return (
    <Card
      className="border-slate-200 hover:shadow-xl hover:-translate-y-0.5 hover:border-gold/40 transition-all duration-300 flex flex-col group"
      data-testid={`service-card-${svc.id}`}
    >
      <CardContent className="p-6 flex flex-col flex-1">
        <div className="w-12 h-12 rounded-lg bg-navy/5 group-hover:bg-gold/15 transition-colors flex items-center justify-center mb-5">
          <Icon className="w-6 h-6 text-navy group-hover:text-gold transition-colors" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-navy mb-2 leading-snug">
          {svc.title}
        </h3>
        <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-1">
          {svc.description}
        </p>
        <div className="pt-4 border-t border-slate-100 mb-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
            Starting from
          </div>
          <div className="font-heading text-xl font-bold text-gold">
            {svc.price}
          </div>
        </div>
        <Button
          type="button"
          onClick={onEnquire}
          className="w-full bg-navy hover:bg-navy-light text-white font-medium"
          data-testid={`service-cta-${svc.id}`}
        >
          Request a Quote
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

const PackageCard = ({ pkg }) => {  const isPremium = pkg.premium;
  return (
    <Card
      className={`relative flex flex-col h-full transition-all duration-300 ${
        isPremium
          ? "border-gold/40 shadow-xl shadow-gold/10 hover:shadow-2xl hover:shadow-gold/20 lg:-translate-y-2 hover:-translate-y-3"
          : "border-slate-200 hover:shadow-lg hover:-translate-y-0.5"
      }`}
      data-testid={`package-card-${pkg.id}`}
    >
      {/* Decorative accent bar */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${pkg.accent} rounded-t-lg`} />

      {pkg.badge && (
        <Badge
          className={`absolute -top-3 right-4 ${
            isPremium
              ? "bg-gold text-navy hover:bg-gold-light"
              : "bg-navy text-white hover:bg-navy-light"
          } font-semibold tracking-wide`}
        >
          {isPremium && <Star className="w-3 h-3 mr-1 fill-current" />}
          {pkg.badge}
        </Badge>
      )}

      <CardContent className="p-6 sm:p-7 flex flex-col flex-1">
        <div className="mb-5">
          <h3 className="font-heading text-2xl font-semibold text-navy mb-1">{pkg.name}</h3>
          <p className="text-sm text-slate-500 uppercase tracking-wider font-medium">{pkg.subtitle}</p>
        </div>

        <div className="mb-5 pb-5 border-b border-slate-200">
          <div
            className={`font-heading text-2xl sm:text-3xl font-bold leading-tight ${
              isPremium ? "text-gold" : "text-navy"
            }`}
          >
            {pkg.price}
          </div>
          {pkg.priceSuffix && (
            <div className="text-slate-500 text-sm mt-1">{pkg.priceSuffix}</div>
          )}
          {pkg.priceMinFee && (
            <div className="text-navy/70 text-xs font-medium mt-1.5">{pkg.priceMinFee}</div>
          )}
          {pkg.onboardingFee && (
            <div
              className={`mt-3 rounded-md px-3 py-2 text-xs leading-relaxed ${
                isPremium ? "bg-gold/10 text-navy" : "bg-navy/5 text-slate-700"
              }`}
              data-testid={`package-${pkg.id}-onboarding-fee`}
            >
              <div className="font-semibold text-navy">+ {pkg.onboardingFee.label}</div>
              <div className={isPremium ? "text-navy/80" : "text-slate-600"}>
                {pkg.onboardingFee.price}
              </div>
            </div>
          )}
        </div>

        <p className="text-slate-600 text-sm mb-6 leading-relaxed">{pkg.description}</p>

        <div className="flex-1 flex flex-col">
          <ul className="space-y-2.5 mb-5" data-testid={`package-${pkg.id}-included`}>
            {pkg.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm">
                <Check
                  className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                    isPremium ? "text-gold" : "text-navy"
                  }`}
                />
                <span className="text-slate-700">{f}</span>
              </li>
            ))}
          </ul>

          {pkg.notIncluded && pkg.notIncluded.length > 0 && (
            <div className="mb-5" data-testid={`package-${pkg.id}-not-included`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Not Included
              </div>
              <ul className="space-y-2">
                {pkg.notIncluded.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <XIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                    <span className="text-slate-500">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {pkg.optionalExtras && pkg.optionalExtras.length > 0 && (
            <div className="mb-5" data-testid={`package-${pkg.id}-extras`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Optional Extras
              </div>
              <ul className="space-y-2">
                {pkg.optionalExtras.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-gold mt-0.5 flex-shrink-0">•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {pkg.includedValue && (
            <div
              className={`mb-5 rounded-lg p-3 text-xs leading-relaxed ${
                isPremium
                  ? "bg-gold/10 text-navy"
                  : "bg-navy/5 text-slate-700"
              }`}
              data-testid={`package-${pkg.id}-value`}
            >
              {pkg.includedValue}
            </div>
          )}
        </div>

        <Button
          asChild
          className={`w-full ${
            isPremium
              ? "bg-gold hover:bg-gold-light text-navy font-semibold"
              : "bg-navy hover:bg-navy-light text-white"
          }`}
          data-testid={`package-cta-${pkg.id}`}
        >
          <Link to={pkg.ctaPath}>
            {pkg.ctaLabel}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default PackagesPage;
